<script setup>
// Картка кліпу в списку: обкладинка з тривалістю, назва, звідки, автор,
// перегляди, лайки й коментарі. На наведення обкладинка оживає — грає сам
// кліп без звуку, як на Twitch
const props = defineProps({
  clip: { type: Object, required: true }
})

const media = useStrapiMedia

const thumb = computed(() => (props.clip.thumbnail ? media(props.clip.thumbnail) : ''))
const videoUrl = computed(() => (props.clip.video?.url ? media(props.clip.video.url) : ''))

const hovering = ref(false)

// Спойлер і 18+: обкладинка розмита, перше натискання лише відкриває
// картку, друге — веде на кліп. Назву ховає тільки спойлер — у 18+ вона
// нічого не видає
const revealed = ref(false)
const hidden = computed(
  () => (props.clip.spoiler || props.clip.adult) && !props.clip.locked && !revealed.value
)
const titleHidden = computed(() => props.clip.spoiler && !revealed.value)
// Ловимо на перехопленні: інакше NuxtLink устигає перейти раніше
const onClick = (e) => {
  if (!hidden.value) return
  e.preventDefault()
  e.stopImmediatePropagation()
  revealed.value = true
}

const source = computed(() =>
  props.clip.sourceType !== 'anime'
    ? props.clip.streamTitle || 'Прямий ефір'
    : `${props.clip.animeTitle || ''}${props.clip.episodeTitle ? ` · ${props.clip.episodeTitle}` : ''}`
)

const ago = computed(() => formatAgo(props.clip.createdAt))
</script>

<template>
  <NuxtLink
    :to="`/clips/${clip.id}`"
    class="group flex flex-col gap-2"
    @mouseenter="hovering = true"
    @mouseleave="hovering = false"
    @click.capture="onClick"
  >
    <div class="relative bg-base-300 rounded-[12px] aspect-video overflow-hidden">
      <img
        v-if="thumb"
        :src="thumb"
        class="w-full h-full object-cover transition-[transform,filter] duration-300"
        :class="hidden ? 'scale-110 blur-xl brightness-75' : 'group-hover:scale-[1.03]'"
        loading="lazy"
        alt=""
      />
      <video
        v-if="hovering && videoUrl && !hidden"
        :src="videoUrl"
        class="absolute inset-0 w-full h-full object-cover"
        autoplay
        muted
        loop
        playsinline
      />
      <span class="top-2 left-2 absolute flex gap-1">
        <span class="bg-black/70 px-1.5 py-0.5 rounded font-semibold text-[10px] text-white uppercase">
          {{ { live: 'ефір', archive: 'архів' }[clip.sourceType] || 'запис' }}
        </span>
        <span
          v-if="clip.adult"
          class="bg-error/90 px-1.5 py-0.5 rounded font-semibold text-[10px] text-error-content"
          title="Кліп для дорослих — лише для тих, хто увійшов"
          >18+</span
        >
      </span>
      <!-- 18+ без входу: ні обкладинки, ні відео сервер не віддав -->
      <span
        v-if="clip.locked"
        class="absolute inset-0 flex flex-col justify-center items-center gap-1 font-semibold text-sm"
        ><span class="bg-black/60 px-2.5 py-1 rounded-full text-white">18+</span
        ><span class="font-normal text-[11px] text-base-content/60">увійдіть, щоб дивитись</span></span
      >
      <span
        v-if="clip.restricted"
        class="top-2 right-2 absolute bg-accent/90 px-1.5 py-0.5 rounded font-semibold text-[10px] text-accent-content uppercase"
        :title="clip.sourceType === 'archive' ? 'Кліп із закритого запису ефіру' : 'Кліп із закритого запису для Монобази'"
        >{{ clip.sourceType === 'archive' ? 'Закритий ефір' : 'Монобаза' }}</span
      >
      <span
        v-if="hidden"
        class="absolute inset-0 flex flex-col justify-center items-center gap-1 font-semibold text-white text-sm"
        ><span class="bg-black/60 px-2.5 py-1 rounded-full">{{ clip.adult ? '18+' : 'Спойлер' }}</span
        ><span class="font-normal text-[11px] text-white/80">натисніть, щоб показати</span></span
      >
      <span
        v-else-if="clip.spoiler"
        class="right-2 absolute bg-warning/90 px-1.5 py-0.5 rounded font-semibold text-[10px] text-warning-content uppercase"
        :class="clip.restricted ? 'top-8' : 'top-2'"
        >спойлер</span
      >
      <span class="right-2 bottom-2 absolute bg-black/70 px-1.5 py-0.5 rounded tabular-nums text-[11px] text-white">
        {{ formatClipTime(clip.duration) }}
      </span>
      <span class="bottom-2 left-2 absolute bg-black/70 px-1.5 py-0.5 rounded tabular-nums text-[11px] text-white">
        {{ clip.views }} перегл.
      </span>
    </div>

    <div class="min-w-0">
      <p
        class="font-semibold group-hover:text-primary text-sm line-clamp-2 transition-[color,filter]"
        :class="{ 'blur-[5px] select-none': titleHidden }"
      >
        {{ clip.title }}
      </p>
      <p class="text-[12px] text-base-content/60 truncate">{{ source }}</p>
      <div class="flex items-center gap-2 mt-0.5 text-[11px] text-base-content/45">
        <span
          class="font-medium truncate"
          :style="{ color: clip.author.color || nickColor(clip.author.name) }"
          >{{ clip.author.name }}</span
        >
        <span>·</span>
        <span class="shrink-0">{{ ago }}</span>
        <span class="flex-1" />
        <span
          class="inline-flex items-center gap-0.5 shrink-0"
          title="Подобається"
          ><ThumbIcon :size="12" /> {{ clip.likes }}</span
        >
        <span
          class="inline-flex items-center gap-0.5 shrink-0"
          title="Коментарі"
          ><svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            aria-hidden="true"
          ><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
          {{ clip.comments || 0 }}</span
        >
      </div>
    </div>
  </NuxtLink>
</template>
